import { useEffect, useState, useCallback } from "react";
import { useUser } from "./User";
import { userService } from "../services/userService";
import { useNavigate } from "react-router-dom";

export const useAuth = () => {
  const { user, setUser, removeUser } = useUser();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Create stable logout function
  const handleLogout = useCallback(() => {
    removeUser();
    navigate('/', { replace: true });
  }, [removeUser, navigate]);

  // Auto-logout effect
  useEffect(() => {
    let logoutTimer;
    
    if (user) {
      // Auto-logout after 1 hour (3600000 ms)
      logoutTimer = setTimeout(handleLogout, 3600000);
    }

    return () => {
      if (logoutTimer) {
        clearTimeout(logoutTimer);
      }
    };
  }, [user, handleLogout]);

  useEffect(() => {
    let mounted = true;

    const fetchUser = async () => {
      try {
        console.log('Fetching user...');
        // First upsert the user to the backend
        await userService.upsert();
        // Check if user is already authenticated by trying to get profile
        const userData = await userService.getProfile();
        console.log('Initial auth check response:', userData);
        
        if (mounted && userData ) {
          const transformedUser = {
            id: userData.id,
            ownerId: userData.ownerId,
            name: userData.fullName || 'Unknown',
            email: userData.email || 'No email'
          };
          console.log('Setting initial user:', transformedUser);
          setUser(transformedUser);
        } else if (mounted) {
          console.log('No initial user data found');
          removeUser();
        }
      } catch (err) {
        if (mounted) {
          console.error('Error in auth flow:', err);
          setError(err);
          removeUser();
          
          if (err.message.includes('401') || err.message.includes('unauthorized')) {
            navigate('/', { replace: true });
          }
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Only fetch if we're not in the process of logging out
    const isLoggingOut = window.location.pathname === '/' && !user;
    if (!user && !isLoggingOut) {
      fetchUser();
    } else {
      setIsLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [setUser, user, removeUser, navigate]);


  return { 
    user, 
    error, 
    isLoading,
    isAuthenticated: !!user
  };
};